import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Modal,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material'
import Spinner from 'components/Spinner/Spinner'
import { formatDistanceToNow } from 'date-fns'
import {
  Platform,
  useGetUserMediaLazyQuery,
  useMediaAccountsQuery,
} from 'graphql/generated/graphql'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import React from 'react'
import { IMediaAccounts } from 'components/PostScheduling/PostScheduling'
import { Sparkles, Image as ImageIcon, Video, Youtube } from 'lucide-react'

interface AutomationSetupProps {
  title: string
  setTitle: React.Dispatch<React.SetStateAction<string>>
  profileValue: IMediaAccounts | undefined
  setProfileValue: React.Dispatch<
    React.SetStateAction<IMediaAccounts | undefined>
  >
  platform: Platform
  workWithValue: 'specific-post' | 'all-publications'
  setWorkWithValue: React.Dispatch<
    React.SetStateAction<'specific-post' | 'all-publications'>
  >
  selectedPublication: string | null
  setSelectedPublication: React.Dispatch<React.SetStateAction<string | null>>
  keyword: string
  setKeyword: React.Dispatch<React.SetStateAction<string>>
}

export default function AutomationSetup({
  title,
  setTitle,
  profileValue,
  setProfileValue,
  platform,
  workWithValue,
  setWorkWithValue,
  setSelectedPublication,
  selectedPublication,
  keyword,
  setKeyword,
}: AutomationSetupProps): JSX.Element {
  const [publicationModal, setPublicationModal] = React.useState(false)

  const { data, loading } = useMediaAccountsQuery({
    variables: {
      platform,
    },
  })
  const [getUserMedia, { loading: userMediaLoading, data: userMediaData }] =
    useGetUserMediaLazyQuery()

  const glassmorphicInput = {
    '& .MuiOutlinedInput-root': {
      background: 'rgba(30, 41, 59, 0.5)',
      backdropFilter: 'blur(12px)',
      borderRadius: '12px',
      color: '#ffffff',
      transition: 'all 0.3s ease',
      '& fieldset': {
        borderColor: 'rgba(168, 85, 247, 0.3)',
        borderWidth: '1px',
      },
      '&:hover': {
        background: 'rgba(30, 41, 59, 0.7)',
        '& fieldset': {
          borderColor: 'rgba(168, 85, 247, 0.5)',
        },
      },
      '&.Mui-focused': {
        background: 'rgba(30, 41, 59, 0.8)',
        boxShadow: '0 0 0 3px rgba(168, 85, 247, 0.1)',
        '& fieldset': {
          borderColor: 'rgba(168, 85, 247, 0.8)',
          borderWidth: '2px',
        },
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
      '&.Mui-focused': {
        color: '#a855f7',
      },
    },
    '& .MuiAutocomplete-tag': {
      background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(236, 72, 153, 0.2))',
      color: '#ffffff',
      border: '1px solid rgba(168, 85, 247, 0.3)',
      borderRadius: '8px',
    },
    '& .MuiAutocomplete-popupIndicator': {
      color: 'rgba(255, 255, 255, 0.6)',
    },
  }

  const glassmorphicRadio = {
    color: 'rgba(255, 255, 255, 0.6)',
    '&.Mui-checked': {
      color: '#a855f7',
    },
    '&:hover': {
      background: 'rgba(168, 85, 247, 0.1)',
    },
  }

  return (
    <Box sx={{ color: '#ffffff' }}>
      {/* Title Input Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          marginBottom: '32px',
          padding: '24px',
          background: 'rgba(30, 41, 59, 0.4)',
          backdropFilter: 'blur(12px)',
          borderRadius: '16px',
          border: '1px solid rgba(168, 85, 247, 0.2)',
          transition: 'all 0.3s ease',
          '&:hover': {
            background: 'rgba(30, 41, 59, 0.5)',
            borderColor: 'rgba(168, 85, 247, 0.3)',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Sparkles size={20} style={{ color: '#a855f7' }} />
          <FormLabel
            id="automation-title"
            sx={{
              color: '#ffffff',
              fontSize: '18px',
              fontWeight: 600,
              '&.Mui-focused': {
                color: '#a855f7',
              },
            }}
          >
            Name Your Automation Tool
          </FormLabel>
        </Box>
        <TextField
          aria-labelledby="automation-title"
          placeholder="e.g., Instagram Lead Generator"
          sx={{
            maxWidth: '600px',
            width: '100%',
            ...glassmorphicInput,
          }}
          value={title}
          onChange={(e) => {
            setTitle(e.target.value)
          }}
        />
      </Box>

      {/* Account Selection Section */}
      <Box
        sx={{
          padding: '24px',
          background: 'rgba(30, 41, 59, 0.4)',
          backdropFilter: 'blur(12px)',
          borderRadius: '16px',
          border: '1px solid rgba(168, 85, 247, 0.2)',
          marginBottom: '24px',
          transition: 'all 0.3s ease',
          '&:hover': {
            background: 'rgba(30, 41, 59, 0.5)',
            borderColor: 'rgba(168, 85, 247, 0.3)',
          },
        }}
      >
        <FormLabel
          id="select-account"
          sx={{
            color: '#ffffff',
            fontSize: '16px',
            fontWeight: 600,
            marginBottom: '16px',
            display: 'block',
          }}
        >
          Select {platform === Platform.Youtube ? 'YouTube' : platform === Platform.Facebook ? 'Facebook' : 'Instagram'} Account
        </FormLabel>
        <Autocomplete
          aria-labelledby="select-account"
          id="account-autocomplete"
          loading={loading}
          options={data?.mediaAccounts ?? []}
          getOptionLabel={(option) => option.accountName}
          filterSelectedOptions
          sx={{
            maxWidth: '600px',
            width: '100%',
            ...glassmorphicInput,
          }}
          value={profileValue}
          onChange={(e, value) => {
            if (value) {
              setProfileValue(value)
            }
          }}
          renderInput={(params) => (
            <TextField {...params} label="Select profile" />
          )}
        />
        {data?.mediaAccounts.length === 0 && !loading && (
          <Box
            sx={{
              marginTop: '16px',
              padding: '12px 16px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '10px',
              color: '#fca5a5',
              fontSize: '14px',
            }}
          >
            Please connect your Facebook business account to continue.
          </Box>
        )}
      </Box>

      {/* Configuration Section */}
      {profileValue && platform !== Platform.Youtube && (
        <Box
          sx={{
            padding: '24px',
            background: 'rgba(30, 41, 59, 0.4)',
            backdropFilter: 'blur(12px)',
            borderRadius: '16px',
            border: '1px solid rgba(168, 85, 247, 0.2)',
          }}
        >
          <FormControl>
            <FormLabel
              id="work-with-group"
              sx={{
                color: '#ffffff',
                fontSize: '16px',
                fontWeight: 600,
                marginBottom: '16px',
                '&.Mui-focused': {
                  color: '#a855f7',
                },
              }}
            >
              This Automation Tool Will Work With
            </FormLabel>
            <RadioGroup
              aria-labelledby="work-with-group"
              name="work-with-radios"
              value={workWithValue}
              onChange={(e) => {
                if (
                  e.target.value === 'specific-post' ||
                  e.target.value === 'all-publications'
                ) {
                  setWorkWithValue(e.target.value)
                  setSelectedPublication(null)
                }
              }}
            >
              <FormControlLabel
                value="specific-post"
                control={<Radio sx={glassmorphicRadio} />}
                label="Specific post or reel"
                sx={{
                  color: '#ffffff',
                  marginBottom: '8px',
                  padding: '8px 12px',
                  borderRadius: '10px',
                  transition: 'background 0.2s',
                  '&:hover': {
                    background: 'rgba(168, 85, 247, 0.05)',
                  },
                }}
              />
              <FormControlLabel
                value="all-publications"
                control={<Radio sx={glassmorphicRadio} />}
                label="All publications"
                sx={{
                  color: '#ffffff',
                  padding: '8px 12px',
                  borderRadius: '10px',
                  transition: 'background 0.2s',
                  '&:hover': {
                    background: 'rgba(168, 85, 247, 0.05)',
                  },
                }}
              />
            </RadioGroup>
          </FormControl>

          {/* Publication Selection */}
          {workWithValue === 'specific-post' && (
            <Box sx={{ marginTop: '24px' }}>
              <FormLabel
                sx={{
                  display: 'block',
                  marginBottom: '12px',
                  color: '#ffffff',
                  fontSize: '15px',
                  fontWeight: 500,
                }}
              >
                Instagram Publication for Comment Tracking
              </FormLabel>
              <Button
                variant="contained"
                sx={{
                  width: '220px',
                  background: selectedPublication
                    ? 'linear-gradient(135deg, #10b981, #059669)'
                    : 'linear-gradient(135deg, #a855f7, #ec4899)',
                  color: '#ffffff',
                  borderRadius: '12px',
                  padding: '12px 24px',
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: selectedPublication
                      ? 'linear-gradient(135deg, #059669, #047857)'
                      : 'linear-gradient(135deg, #9333ea, #db2777)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(168, 85, 247, 0.4)',
                  },
                }}
                onClick={() => {
                  void getUserMedia({
                    variables: {
                      accountId: profileValue.id,
                      platform,
                    },
                  })
                  setPublicationModal(true)
                }}
              >
                {selectedPublication ? (
                  <>
                    <CheckCircleIcon sx={{ marginRight: '8px' }} />
                    Selected
                  </>
                ) : (
                  'Select Publication'
                )}
              </Button>
            </Box>
          )}

          {/* Keyword Input */}
          <FormControl
            sx={{
              marginTop: '24px',
              width: '100%',
            }}
          >
            <FormLabel
              id="trigger-keyword-label"
              sx={{
                color: '#ffffff',
                fontSize: '15px',
                fontWeight: 500,
                marginBottom: '12px',
                display: 'block',
              }}
            >
              Trigger Keyword for Comments
            </FormLabel>
            <TextField
              aria-labelledby="trigger-keyword-label"
              placeholder="Enter keyword to trigger automation"
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value)
              }}
              sx={{
                maxWidth: '600px',
                width: '100%',
                ...glassmorphicInput,
              }}
            />
          </FormControl>
        </Box>
      )}

      {/* Publication Selection Modal */}
      <Modal
        open={publicationModal}
        onClose={() => {
          setPublicationModal(false)
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 700,
            maxHeight: '85vh',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            padding: '32px',
            borderRadius: '20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            bgcolor: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(168, 85, 247, 0.3)',
            color: '#ffffff',
          }}
        >
          {userMediaLoading ? (
            <Spinner />
          ) : (
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  marginBottom: '24px',
                }}
              >
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ImageIcon size={20} color="#ffffff" />
                </Box>
                <Box>
                  <h3 style={{ margin: 0, color: '#ffffff', fontSize: '24px', fontWeight: 700 }}>
                    Select Publication
                  </h3>
                  <p style={{ margin: '4px 0 0 0', color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
                    Choose a post or reel to track comments
                  </p>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '16px',
                  maxHeight: '520px',
                  overflowY: 'auto',
                  padding: '8px',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'rgba(30, 41, 59, 0.3)',
                    borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(168, 85, 247, 0.5)',
                    borderRadius: '10px',
                    '&:hover': {
                      background: 'rgba(168, 85, 247, 0.7)',
                    },
                  },
                }}
              >
                {userMediaData?.getUserMedia.map((item) => (
                  <Box
                    key={item?.id}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      border: '1px solid rgba(168, 85, 247, 0.2)',
                      borderRadius: '12px',
                      padding: '12px',
                      background: 'rgba(30, 41, 59, 0.4)',
                      backdropFilter: 'blur(12px)',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-4px) scale(1.02)',
                        borderColor: 'rgba(168, 85, 247, 0.5)',
                        boxShadow: '0 8px 24px rgba(168, 85, 247, 0.3)',
                        background: 'rgba(30, 41, 59, 0.6)',
                      },
                    }}
                    onClick={() => {
                      if (item?.id) {
                        setSelectedPublication(item.id)
                        setPublicationModal(false)
                      }
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px',
                      }}
                    >
                      <Box
                        sx={{
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontSize: '12px',
                        }}
                      >
                        {item?.timestamp &&
                          formatDistanceToNow(new Date(item.timestamp), {
                            addSuffix: true,
                          })}
                      </Box>
                      {item?.media_type === 'VIDEO' ? (
                        <Box
                          sx={{
                            background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                            color: '#ffffff',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          <Video size={12} />
                          VIDEO
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            color: '#ffffff',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          <ImageIcon size={12} />
                          IMAGE
                        </Box>
                      )}
                    </Box>
                    <img
                      src={item?.media_url}
                      alt={item?.caption ?? 'Instagram media'}
                      loading="lazy"
                      style={{
                        width: '100%',
                        height: '164px',
                        borderRadius: '10px',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          {/* For YouTube, always works on all videos */}
          {profileValue && platform === Platform.Youtube && (
            <Box
              sx={{
                padding: '20px 24px',
                background: 'rgba(30, 41, 59, 0.4)',
                backdropFilter: 'blur(12px)',
                borderRadius: '12px',
                border: '1px solid rgba(168, 85, 247, 0.2)',
                marginBottom: '24px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #ff0000, #cc0000)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Youtube size={20} color="#ffffff" />
                </Box>
                <Box>
                  <Box sx={{ fontSize: '16px', fontWeight: 600, color: '#ffffff' }}>
                    Monitors All Channel Videos
                  </Box>
                  <Box sx={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.6)', mt: 0.5 }}>
                    This automation will check comments on all videos in your channel
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  )
}